import Swal from "sweetalert2";

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Slider,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import RoomServiceIcon from "@mui/icons-material/RoomService";
import ViewKanbanIcon from "@mui/icons-material/ViewKanban";
import GridViewIcon from "@mui/icons-material/GridView";
import OutdoorGrillIcon from "@mui/icons-material/OutdoorGrill";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import DeliveryDiningIcon from "@mui/icons-material/DeliveryDining";
import PrintIcon from "@mui/icons-material/Print";
import { Volume2, VolumeX, Upload, Settings2, X } from "lucide-react";
import { useInstantOrderAlerts } from "../../context/InstantOrderAlertsContext";
import { ORDER_SOUND_PRESETS } from "../../utils/instantOrderSound";
import Layout from "../../components/layout/Layout";
import instantOrdersApi from "../../api/instantOrders";
import deliveryAgentsApi from "../../api/delivery-agents";
import kitchensApi from "../../api/kitchens";
import useAuth from "../../hooks/useAuth";
import { io, Socket } from "socket.io-client";

const queueStatuses = ["PLACED", "ACCEPTED", "PREPARING", "READY", "PICKED_UP"] as const;

const OrderTimer = ({ createdAt, status, basePrepTime }: { createdAt: string; status: string; basePrepTime?: number }) => {
  const [elapsed, setElapsed] = useState("");
  const [isAlert, setIsAlert] = useState(false);

  useEffect(() => {
    const targetPrepSeconds = (basePrepTime || 10) * 60;

    const updateTimer = () => {
      const diffMs = Date.now() - new Date(createdAt).getTime();
      const diffSecs = Math.floor(diffMs / 1000);
      const minutes = Math.floor(diffSecs / 60);
      const seconds = diffSecs % 60;
      const formatted = `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;

      if (status === "PLACED") {
        setElapsed(`Waiting ${formatted}`);
        setIsAlert(minutes >= 3);
      } else if (["ACCEPTED", "PREPARING"].includes(status)) {
        const remaining = targetPrepSeconds - diffSecs;
        const absRem = Math.abs(remaining);
        const m = String(Math.floor(absRem / 60)).padStart(2, "0");
        const s = String(absRem % 60).padStart(2, "0");
        
        if (remaining < 0) {
          setElapsed(`Overdue ${m}:${s}`);
          setIsAlert(true);
        } else {
          setElapsed(`${m}:${s} left`);
          setIsAlert(false);
        }
      } else {
        setElapsed(`${formatted} total`);
        setIsAlert(false);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [createdAt, status, basePrepTime]);

  return (
    <Typography
      variant="caption"
      fontWeight={800}
      sx={{
        color: isAlert ? "#dc2626" : "text.secondary",
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        backgroundColor: isAlert ? "#fee2e2" : "#f1f5f9",
        px: 1,
        py: 0.25,
        borderRadius: 1.5,
      }}
    >
      <TimerOutlinedIcon sx={{ fontSize: 13, color: isAlert ? "#dc2626" : "text.secondary" }} />
      {elapsed}
    </Typography>
  );
};


const handlePrintLabel = async (order: any) => {
  try {
    Swal.fire({
      title: "Generating KOT",
      text: "Please wait...",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    const pdfData = await instantOrdersApi.downloadLabel(order._id);
    const pdfBlob = new Blob([pdfData], { type: "application/pdf" });
    const url = URL.createObjectURL(pdfBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `kot-${order.orderNumber}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    const printFrame = document.createElement("iframe");
    printFrame.style.position = "fixed";
    printFrame.style.width = "0";
    printFrame.style.height = "0";
    printFrame.style.border = "0";
    printFrame.src = url;
    document.body.appendChild(printFrame);

    let cleanedUp = false;
    const cleanup = () => {
      if (cleanedUp) return;
      cleanedUp = true;
      URL.revokeObjectURL(url);
      printFrame.remove();
    };

    let printed = false;
    const triggerPrint = () => {
      if (printed) return;
      printed = true;
      try {
        printFrame.contentWindow?.focus();
        printFrame.contentWindow?.print();
      } catch (printError) {
        console.error("Failed to open print dialog", printError);
      }
    };

    printFrame.onload = () => {
      triggerPrint();
      printFrame.contentWindow?.addEventListener("afterprint", cleanup);
    };
    setTimeout(triggerPrint, 750);
    setTimeout(cleanup, 60000);

    Swal.close();
  } catch (error) {
    console.error("Failed to download PDF label", error);
    Swal.fire({
      title: "Error",
      text: "Could not generate PDF KOT. Please try again.",
      icon: "error",
    });
  }
};

interface OrderCardProps {
  order: any;
  orders: any[];
  updatingOrderId: string | null;
  handleStatusUpdate: (id: string, status: string) => void;
  riderDrafts: Record<string, string>;
  setRiderDrafts: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  deliveryAgents: any[];
  handleAssignRider: (id: string) => void;
  setSelectedOrder: (order: any) => void;
  isAdminUser: boolean;
  isKanban?: boolean;
  kitchen?: any;
}

export const OrderCard = ({
  order,
  orders,
  updatingOrderId,
  handleStatusUpdate,
  riderDrafts,
  setRiderDrafts,
  deliveryAgents,
  handleAssignRider,
  setSelectedOrder,
  isAdminUser,
  isKanban = false,
  kitchen,
}: OrderCardProps) => {
  const [secondsElapsed, setSecondsElapsed] = useState(0);
  const [riderExpanded, setRiderExpanded] = useState(false);
  const itemCount = [...(order.items || []), ...(order.addOns || [])].reduce((sum, item) => sum + Number(item.quantity || 0), 0);

  useEffect(() => {
    if (!["PLACED", "ACCEPTED", "PREPARING"].includes(order.status)) return;
    const updateTimer = () => {
      const diff = Math.floor((Date.now() - new Date(order.createdAt).getTime()) / 1000);
      setSecondsElapsed(diff);
    };
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [order.createdAt, order.status]);

  const minutesPlaced = Math.floor(secondsElapsed / 60);
  // SLA Calculation
  const basePrepTime = kitchen?.logisticsConfig?.basePrepTime || 10;
  const targetPrepSeconds = basePrepTime * 60;
  const isPrepBreached = ["ACCEPTED", "PREPARING"].includes(order.status) && secondsElapsed >= targetPrepSeconds;

  const isRedAlert = (order.status === "PLACED" && minutesPlaced >= 3) || isPrepBreached;
  const isApproaching = order.status === "PLACED" ? secondsElapsed >= 120 : ["ACCEPTED", "PREPARING"].includes(order.status) && secondsElapsed >= targetPrepSeconds * 0.8;

  return (
    <Card
      data-testid={`instant-order-card-${order._id}`}
      sx={{
        // Keep content-sized cards from compressing inside scrollable Kanban columns.
        flexShrink: 0,
        height: "auto",
        borderRadius: 2,
        backgroundColor: "#ffffff",
        borderColor: isRedAlert ? "#ef4444" : isApproaching ? "#f59e0b" : "#e2e8f0",
        borderWidth: "1px",
        borderLeftWidth: "3px",
        borderStyle: "solid",
        boxShadow: "none",
        transition: "all 0.25s ease",
      }}
    >
      <CardContent sx={{ p: isKanban ? 2 : 2.5, "&:last-child": { pb: isKanban ? 2 : 2.5 } }}>
        <Stack spacing={1.5}>
          {/* Top Row: Order Number, Customer, Status Chip */}
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
            <Box sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
              <Typography variant="subtitle1" fontWeight={850} color="#0f172a" sx={{ lineHeight: 1.2 }}>
                #{order.orderNumber}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.82rem" }}>
                {order.userId?.name || "Customer"} {order.userId?.phoneNumber ? `• ${order.userId.phoneNumber}` : ""}
              </Typography>
            </Box>
            <Chip
              label={isRedAlert ? "Overdue" : isApproaching ? "Due soon" : order.status.replace("_", " ")}
              color={isRedAlert ? "error" : isApproaching ? "warning" : "default"}
              variant="outlined"
              size="small"
              sx={{ fontWeight: 800, fontSize: "0.7rem", height: 24 }}
            />
          </Stack>

          {/* Time & Item Count Pills */}
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
            <Chip
              size="small"
              icon={<AccessTimeIcon sx={{ fontSize: "14px !important" }} />}
              label={new Date(order.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              variant="outlined"
              sx={{ fontSize: "0.75rem", height: 22 }}
            />
            <Chip
              size="small"
              icon={<RoomServiceIcon sx={{ fontSize: "14px !important" }} />}
              label={`${itemCount} ${itemCount === 1 ? "item" : "items"}`}
              variant="outlined"
              sx={{ fontSize: "0.75rem", height: 22 }}
            />
            <OrderTimer 
              createdAt={order.createdAt} 
              status={order.status} 
              basePrepTime={kitchen?.logisticsConfig?.basePrepTime || 10} 
            />
          </Stack>

          {/* Primary Pipeline Action Button */}
          <Box sx={{ mt: 1 }}>
            {order.status === "PLACED" && (
              <Button
                variant="contained"
                color="warning"
                fullWidth
                disabled={updatingOrderId === order._id}
                onClick={() => void handleStatusUpdate(order._id, "PREPARING")}
                startIcon={updatingOrderId === order._id ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutlineIcon fontSize="small" />}
                sx={{
                  fontWeight: 800,
                  py: 1,
                  minHeight: 44,
                  fontSize: "0.85rem",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": { boxShadow: "0 2px 6px rgba(245, 158, 11, 0.3)" },
                }}
              >
                Accept & Start Prep
              </Button>
            )}

            {order.status === "ACCEPTED" && (
              <Button
                variant="contained"
                color="primary"
                fullWidth
                disabled={updatingOrderId === order._id}
                onClick={() => void handleStatusUpdate(order._id, "PREPARING")}
                startIcon={updatingOrderId === order._id ? <CircularProgress size={16} color="inherit" /> : <OutdoorGrillIcon fontSize="small" />}
                sx={{
                  fontWeight: 800,
                  py: 1,
                  minHeight: 44,
                  fontSize: "0.85rem",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": { boxShadow: "0 2px 6px rgba(59, 130, 246, 0.3)" },
                }}
              >
                Start Preparation
              </Button>
            )}

            {order.status === "PREPARING" && (
              <Button
                variant="contained"
                color="success"
                fullWidth
                disabled={updatingOrderId === order._id}
                onClick={() => void handleStatusUpdate(order._id, "READY")}
                startIcon={updatingOrderId === order._id ? <CircularProgress size={16} color="inherit" /> : <Inventory2OutlinedIcon fontSize="small" />}
                sx={{
                  fontWeight: 800,
                  py: 1,
                  minHeight: 44,
                  fontSize: "0.85rem",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": { boxShadow: "0 2px 6px rgba(34, 197, 94, 0.3)" },
                }}
              >
                Mark Ready at Pass
              </Button>
            )}

            {order.status === "READY" && (
              <Button
                variant="contained"
                fullWidth
                disabled={updatingOrderId === order._id}
                onClick={() => void handleStatusUpdate(order._id, "PICKED_UP")}
                startIcon={updatingOrderId === order._id ? <CircularProgress size={16} color="inherit" /> : <DeliveryDiningIcon fontSize="small" />}
                sx={{
                  fontWeight: 800,
                  py: 1,
                  minHeight: 44,
                  fontSize: "0.85rem",
                  textTransform: "none",
                  backgroundColor: "#10b981",
                  "&:hover": { backgroundColor: "#059669", boxShadow: "0 2px 6px rgba(16, 185, 129, 0.3)" },
                  boxShadow: "none",
                }}
              >
                Hand to Delivery Partner
              </Button>
            )}

            {order.status === "PICKED_UP" && (
              <Button
                variant="contained"
                fullWidth
                disabled={updatingOrderId === order._id}
                onClick={() => void handleStatusUpdate(order._id, "DELIVERED")}
                startIcon={updatingOrderId === order._id ? <CircularProgress size={16} color="inherit" /> : <TaskAltIcon fontSize="small" />}
                sx={{
                  fontWeight: 800,
                  py: 1,
                  minHeight: 44,
                  fontSize: "0.85rem",
                  textTransform: "none",
                  backgroundColor: "#6366f1",
                  "&:hover": { backgroundColor: "#4f46e5", boxShadow: "0 2px 6px rgba(99, 102, 241, 0.3)" },
                  boxShadow: "none",
                }}
              >
                Mark Delivered
              </Button>
            )}
          </Box>

          {/* Kitchen Priority Meal List */}
          <Box
            sx={{
              py: 0.5,
              overflowWrap: "anywhere",
            }}
          >
            <Typography
              variant="caption"
              fontWeight={800}
              color="#64748b"
              sx={{ display: "block", mb: 0.75, fontSize: "0.7rem", letterSpacing: 0, textTransform: "uppercase" }}
            >
              Order items
            </Typography>
            <Stack spacing={0.5}>
              {[...(order.items || []), ...(order.addOns || [])].map((item: any, index: number) => (
                <Box key={`${item.instantMealId || item.name}-${index}`} sx={{ mb: 0.25 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box sx={{ display: "flex", alignItems: "center", flex: 1, mr: 1 }}>
                      {item.itemTypeRef === "NowCombo" ? (
                        <LayersOutlinedIcon sx={{ fontSize: 15, color: "#ea580c", mr: 0.75, flexShrink: 0 }} />
                      ) : (
                        <Box sx={{ width: 5, height: 5, borderRadius: "50%", backgroundColor: "#94a3b8", mr: 1, flexShrink: 0 }} />
                      )}
                      <Typography fontWeight={750} variant="body2" sx={{ color: "#0f172a", fontSize: "0.95rem", lineHeight: 1.4 }}>
                        <span style={{ color: "#0f172a", fontWeight: 800, fontSize: 18, marginRight: 6 }}>{item.quantity}x</span>
                        {item.name}
                        {item.selectedSide && (
                          <span style={{ color: "#d97706", fontWeight: 700, fontSize: "0.8rem", marginLeft: 4 }}>
                            ({item.selectedSide.name})
                          </span>
                        )}
                      </Typography>
                    </Box>
                  </Box>
                  {item.itemTypeRef === "NowCombo" && Array.isArray(item.comboItems) && item.comboItems.length > 0 && (
                    <Box sx={{ pl: 1.75, mt: 0.25, borderLeft: "2px dashed #f97316" }}>
                      {item.comboItems.map((sub: any, subIdx: number) => (
                        <Typography key={subIdx} variant="caption" sx={{ display: "block", color: "#475569", fontWeight: 600, fontSize: "0.75rem" }}>
                          └─ {sub.quantity > 1 ? `${sub.quantity}x ` : ""}{sub.name}
                          {sub.selectedSide?.name && <span style={{ color: "#d97706" }}> ({sub.selectedSide.name})</span>}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              ))}
            </Stack>

            {/* Special Instruction / Notes Alert */}
            {order.notes && (
              <Box sx={{ mt: 1, p: 0.75, px: 1, borderRadius: 1.5, backgroundColor: "#fffbeb", border: "1px solid #fde68a" }}>
                <Typography variant="caption" fontWeight={850} color="#b45309" sx={{ display: "block" }}>
                  NOTE:
                </Typography>
                <Typography variant="caption" fontWeight={700} color="#78350f" sx={{ display: "block" }}>
                  {order.notes}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Rider Status or Quick Assignment */}
          {order.riderId ? (
            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>Rider: <strong>{order.riderId.name}</strong></Typography>
          ) : (
            <Box>
            {order.status !== "READY" && <Button size="small" onClick={() => setRiderExpanded(value => !value)} aria-expanded={riderExpanded}>{riderExpanded ? "Hide rider assignment" : "Assign rider"}</Button>}
            <Collapse in={order.status === "READY" || riderExpanded}>
            <Box
              sx={{
                p: 1.25,
                borderRadius: 2.5,
                borderColor: order.status === "READY" ? "#fed7aa" : "#e2e8f0",
                backgroundColor: order.status === "READY" ? "#fffbeb" : "#fafafa",
              }}
            >
              {order.status === "READY" && (
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.75 }}>
                  <WarningAmberIcon sx={{ fontSize: 15, color: "#d97706" }} />
                  <Typography variant="caption" fontWeight={800} color="#b45309">
                    Order is ready at pass. Assign delivery partner:
                  </Typography>
                </Stack>
              )}
              <Stack direction={isKanban ? "column" : { xs: "column", sm: "row" }} spacing={1}>
                <FormControl size="small" fullWidth>
                  <InputLabel id={`rider-label-${order._id}`} sx={{ fontSize: "0.8rem" }}>Select Rider</InputLabel>
                  <Select
                    labelId={`rider-label-${order._id}`}
                    value={riderDrafts[order._id] || ""}
                    label="Select Rider"
                    sx={{ fontSize: "0.82rem" }}
                    onChange={(e) =>
                      setRiderDrafts((prev) => ({ ...prev, [order._id]: e.target.value }))
                    }
                  >
                    <MenuItem value="">Unassigned</MenuItem>
                    {deliveryAgents.map((agent) => {
                      const activeCount = agent.activeInstantOrdersCount || 0;
                      let riderStatus = agent.instantStatus || "OFF_DUTY";
                      if (agent.isOnline && riderStatus === "OFF_DUTY") {
                        riderStatus = "AVAILABLE";
                      }
                      if (activeCount > 0) {
                        riderStatus = "ON_DELIVERY";
                      }
                      const dotColor =
                        riderStatus === "AVAILABLE"
                          ? "#22c55e"
                          : riderStatus === "ON_DELIVERY"
                          ? "#f59e0b"
                          : "#64748b";

                      const carryingOrders = orders.filter(
                        (o) => o.riderId && (o.riderId._id === agent._id || o.riderId === agent._id)
                      );
                      const carryingList = carryingOrders.map((o) => o.orderNumber).join(", ");
                      const carryingText = carryingOrders.length > 0 ? ` (${carryingList})` : "";

                      return (
                        <MenuItem key={agent._id} value={agent._id}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: dotColor }} />
                            <Typography variant="body2" sx={{ fontSize: "0.82rem" }}>
                              {agent.name} {carryingText}
                            </Typography>
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={updatingOrderId === order._id || !riderDrafts[order._id]}
                  onClick={() => void handleAssignRider(order._id)}
                  sx={{ textTransform: "none", fontWeight: 700, whiteSpace: "nowrap" }}
                >
                  Assign
                </Button>
              </Stack>
            </Box>
            </Collapse>
            </Box>
          )}

          {/* Secondary Action Toolbar: Print KOT and View Details */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap gap={0.5} pt={0.5}>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Print KOT"><IconButton aria-label="Print KOT" size="small" onClick={() => handlePrintLabel(order)}><PrintIcon fontSize="small" /></IconButton></Tooltip>
              <Button
                size="small"
                variant="text"
                onClick={() => setSelectedOrder(order)}
                sx={{ textTransform: "none", fontSize: "0.75rem", px: 1 }}
              >
                Details
              </Button>
            </Stack>

          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

interface KanbanStageConfig {
  id: string;
  title: string;
  statuses: string[];
  icon: React.ReactNode;
  headerBg: string;
  borderColor: string;
  headerAccent: string;
  subtitle: string;
  emptyText: string;
}

const kanbanStages: KanbanStageConfig[] = [
  {
    id: "new",
    title: "1. New / Action",
    statuses: ["PLACED"],
    icon: <NotificationsActiveIcon sx={{ color: "#d97706", fontSize: 18 }} />,
    headerBg: "#fffbeb",
    borderColor: "#fde68a",
    headerAccent: "#f59e0b",
    subtitle: "Requires immediate acceptance",
    emptyText: "No pending orders requiring action.",
  },
  {
    id: "prep",
    title: "2. In Kitchen",
    statuses: ["ACCEPTED", "PREPARING"],
    icon: <OutdoorGrillIcon sx={{ color: "#2563eb", fontSize: 18 }} />,
    headerBg: "#eff6ff",
    borderColor: "#bfdbfe",
    headerAccent: "#3b82f6",
    subtitle: "Cooking & meal assembly",
    emptyText: "No meals currently in preparation.",
  },
  {
    id: "ready",
    title: "3. Ready at Pass",
    statuses: ["READY"],
    icon: <CheckCircleIcon sx={{ color: "#16a34a", fontSize: 18 }} />,
    headerBg: "#f0fdf4",
    borderColor: "#bbf7d0",
    headerAccent: "#10b981",
    subtitle: "Packed & awaiting rider pickup",
    emptyText: "No orders waiting at the pass.",
  },
  {
    id: "transit",
    title: "4. Out for Delivery",
    statuses: ["PICKED_UP"],
    icon: <DeliveryDiningIcon sx={{ color: "#4f46e5", fontSize: 18 }} />,
    headerBg: "#eef2ff",
    borderColor: "#c7d2fe",
    headerAccent: "#6366f1",
    subtitle: "In transit to customer",
    emptyText: "No orders currently in transit.",
  },
];

const InstantOrdersQueue = () => {
  const { admin, selectedKitchenId } = useAuth();
  const isAdminUser = admin?.role === "admin" || admin?.role === "super-admin";
  const [orders, setOrders] = useState<any[]>([]);
  const [deliveryAgents, setDeliveryAgents] = useState<any[]>([]);
  const [kitchen, setKitchen] = useState<any | null>(null);
  const [toggleDialogOpen, setToggleDialogOpen] = useState(false);
  const [togglingStatus, setTogglingStatus] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [status, setStatus] = useState("");
  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
  const [updatingOrderId, setUpdatingOrderId] = useState<string | null>(null);
  const [riderDrafts, setRiderDrafts] = useState<Record<string, string>>({});
  const sound = useInstantOrderAlerts();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [activeStage, setActiveStage] = useState("new");
  const narrowBoard = useMediaQuery("(max-width: 1250px)");
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);
  const boardRef = useRef<HTMLDivElement | null>(null);
  const [boardHeight, setBoardHeight] = useState(360);
  const [refreshing, setRefreshing] = useState(false);
  const [clock, setClock] = useState(Date.now());
  useEffect(() => { const timer = window.setInterval(() => setClock(Date.now()), 1000); return () => window.clearInterval(timer); }, []);
  const overdueCount = orders.filter(order => ["PLACED", "ACCEPTED", "PREPARING"].includes(order.status) && clock - new Date(order.createdAt).getTime() >= (order.status === "PLACED" ? 3 : kitchen?.logisticsConfig?.basePrepTime || 10) * 60000).length;
  const waitingTimes = orders.filter(order => order.status === "PLACED").map(order => new Date(order.createdAt).getTime()).filter(Number.isFinite);
  const oldestWaiting = waitingTimes.length ? Math.max(0, Math.floor((clock - Math.min(...waitingTimes)) / 60000)) : null;
  const fetchRequest = useRef(0);
  const refreshQueueRef = useRef<() => Promise<void>>(async () => {});
  const filtersRef = useRef({ search: debouncedSearch, status, selectedKitchenId });
  filtersRef.current = { search: debouncedSearch, status, selectedKitchenId };

  // View mode switcher (Kanban Pipeline vs Classic Grid)
  const [viewMode, setViewMode] = useState<"KANBAN" | "GRID">(() => {
    return (localStorage.getItem("instantOrdersViewMode") as "KANBAN" | "GRID") || "KANBAN";
  });

  // Hybrid WebSocket state & safety polling
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const [pollingEnabled, setPollingEnabled] = useState(
    () => localStorage.getItem("instantOrdersPolling") !== "false"
  );
  useLayoutEffect(() => {
    const board = boardRef.current;
    if (!board) return;
    const measure = () => setBoardHeight(Math.max(280, window.innerHeight - board.getBoundingClientRect().top - window.scrollY - 16));
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    if (board.parentElement) observer?.observe(board.parentElement);
    window.addEventListener("resize", measure);
    return () => { observer?.disconnect(); window.removeEventListener("resize", measure); };
  }, [viewMode, narrowBoard, loading, error, isSocketConnected, sound.enabled, sound.blocked]);


  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 250);

    return () => clearTimeout(timer);
  }, [search]);

  const handleViewModeChange = (
    event: React.MouseEvent<HTMLElement>,
    nextMode: "KANBAN" | "GRID" | null
  ) => {
    if (nextMode !== null) {
      setViewMode(nextMode);
      localStorage.setItem("instantOrdersViewMode", nextMode);
    }
  };

  const fetchQueue = useCallback(async () => {
    const request = ++fetchRequest.current;
    try {
      setError(null);
      setRefreshing(true);

      // Fetch active kitchen settings/toggle state
      const kitchenResponse = await kitchensApi.getAccessibleKitchens();
      const activeKitchen = kitchenResponse.kitchens?.find((k) => k.isActive && (!selectedKitchenId || k._id === selectedKitchenId));
      if (request !== fetchRequest.current) return;
      setKitchen(activeKitchen || null);

      const data = await instantOrdersApi.getQueue({
        ...(selectedKitchenId ? { kitchenId: selectedKitchenId } : {}),
        search: debouncedSearch || undefined,
        status: status || undefined,
      });
      const nextOrders = data.orders || [];
      if (request !== fetchRequest.current) return;
      setOrders(nextOrders);
      setLastUpdated(Date.now());
      setRiderDrafts((prev) => {
        const next = { ...prev };
        nextOrders.forEach((order: any) => {
          if (!(order._id in next)) {
            next[order._id] = order.riderId?._id || "";
          }
        });
        return next;
      });
    } catch (fetchError) {
      if (request !== fetchRequest.current) return;
      console.error("Failed to fetch instant order queue", fetchError);
      setError("Could not fetch Kinnow Instant order queue");
    } finally {
      if (request === fetchRequest.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [debouncedSearch, status, selectedKitchenId]);
  refreshQueueRef.current = fetchQueue;

  useLayoutEffect(() => {
    ++fetchRequest.current;
    setOrders([]);
    setKitchen(null);
    setLoading(true);
  }, [selectedKitchenId]);

  // Initial load
  useEffect(() => {
    void fetchQueue();
  }, [fetchQueue]);

  // Load delivery agents
  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const response = await deliveryAgentsApi.getDeliveryAgents({
          isActive: true,
          page: 1,
          limit: 200,
        });
        setDeliveryAgents(response.deliveryAgents || []);
      } catch (fetchError) {
        console.error("Failed to fetch delivery agents", fetchError);
      }
    };

    void fetchAgents();
  }, []);

  // Real-time WebSocket connection to admin:live room
  useEffect(() => {
    const API_URL = process.env.REACT_APP_API_URL || window.location.origin;
    const token = localStorage.getItem("adminToken");

    if (!token) return;

    const socket = io(API_URL, {
      auth: { token },
      transports: ["websocket", "polling"],
      reconnectionAttempts: 15,
      reconnectionDelay: 2000,
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      setIsSocketConnected(true);
      socket.emit("admin:join");
      void refreshQueueRef.current();
      console.log("[InstantQueue] Connected to live admin socket room");
    });

    socket.on("disconnect", () => {
      setIsSocketConnected(false);
      console.log("[InstantQueue] Disconnected from admin socket room");
    });

    socket.on("connect_error", (err) => {
      console.warn("[InstantQueue] Socket connect error:", err.message);
      setIsSocketConnected(false);
    });

    // Sound is handled globally; this connection updates the queue UI.
    socket.on("instant_order:new", (newOrder: any) => {
      if (!newOrder || !newOrder._id) return;
      if (filtersRef.current.search || filtersRef.current.status || filtersRef.current.selectedKitchenId) {
        void refreshQueueRef.current();
        return;
      }
      setLastUpdated(Date.now());
      void refreshQueueRef.current();
      setOrders((prev) => {
        if (prev.some((o) => o._id === newOrder._id)) return prev;
        return [newOrder, ...prev];
      });
    });

    // Real-time: Status updated -> update state immediately
    socket.on("instant_order:status", (updatedOrder: any) => {
      if (!updatedOrder || !updatedOrder._id) return;
      if (filtersRef.current.search || filtersRef.current.status || filtersRef.current.selectedKitchenId) {
        void refreshQueueRef.current();
        return;
      }
      setLastUpdated(Date.now());
      void refreshQueueRef.current();
      setOrders((prev) => {
        if (["DELIVERED", "CANCELLED"].includes(updatedOrder.status)) {
          return prev.filter((o) => o._id !== updatedOrder._id);
        }
        const exists = prev.some((o) => o._id === updatedOrder._id);
        if (exists) {
          return prev.map((o) => (o._id === updatedOrder._id ? updatedOrder : o));
        }
        return [updatedOrder, ...prev];
      });
    });

    // Real-time: Rider assigned
    socket.on("instant_order:assigned", (updatedOrder: any) => {
      if (!updatedOrder || !updatedOrder._id) return;
      if (filtersRef.current.selectedKitchenId) { void refreshQueueRef.current(); return; }
      setLastUpdated(Date.now());
      setOrders((prev) =>
        prev.map((o) => (o._id === updatedOrder._id ? updatedOrder : o))
      );
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  // Fail-safe background polling (45s when socket live, 15s if socket disconnected)
  useEffect(() => {
    localStorage.setItem("instantOrdersPolling", pollingEnabled ? "true" : "false");
    if (!pollingEnabled) {
      return undefined;
    }

    const pollIntervalMs = isSocketConnected ? 45000 : 15000;
    const interval = window.setInterval(() => {
      void fetchQueue();
    }, pollIntervalMs);

    return () => window.clearInterval(interval);
  }, [pollingEnabled, isSocketConnected, fetchQueue]);

  const stats = useMemo(
    () => ({
      total: orders.length,
      placed: orders.filter((order) => order.status === "PLACED").length,
      preparing: orders.filter((order) => ["ACCEPTED", "PREPARING"].includes(order.status)).length,
      ready: orders.filter((order) => order.status === "READY").length,
      pickedUp: orders.filter((order) => order.status === "PICKED_UP").length,
    }),
    [orders]
  );

  const handleStatusUpdate = async (orderId: string, nextStatus: string) => {
    try {
      setUpdatingOrderId(orderId);
      await instantOrdersApi.updateStatus(orderId, nextStatus);
      await refreshQueueRef.current();
    } catch (updateError: any) {
      setError(updateError?.response?.data?.message || "Could not update order status");
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const handleAssignRider = async (orderId: string) => {
    try {
      const riderId = riderDrafts[orderId];
      if (!riderId) {
        setError("Please choose a rider first");
        return;
      }
      setUpdatingOrderId(orderId);
      await instantOrdersApi.assignRider(orderId, riderId);
      await refreshQueueRef.current();
    } catch (assignError: any) {
      setError(assignError?.response?.data?.message || "Could not assign rider");
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const handleToggleSwitch = async (newVal: boolean) => {
    if (!newVal) {
      setToggleDialogOpen(true);
    } else {
      await updateKitchenStatus(true);
    }
  };

  const confirmGoOffline = async () => {
    await updateKitchenStatus(false);
    setToggleDialogOpen(false);
  };

  const updateKitchenStatus = async (isOpen: boolean) => {
    try {
      setTogglingStatus(true);
      setError(null);
      await instantOrdersApi.toggleKitchenStatus(isOpen, kitchen?._id);
      await refreshQueueRef.current();
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to update kitchen status");
    } finally {
      setTogglingStatus(false);
    }
  };

  const [togglingRain, setTogglingRain] = useState(false);
  const handleToggleRainMode = async (newVal: boolean) => {
    if (!kitchen) return;
    try {
      setTogglingRain(true);
      setError(null);
      await kitchensApi.toggleRainMode(kitchen._id, newVal);
      await refreshQueueRef.current();
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to toggle rain mode");
    } finally {
      setTogglingRain(false);
    }
  };

  return (
    <Layout>
      <Box sx={{ pb: 1 }}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} gap={1.5} mb={2}>
          <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
            <Typography variant="h5" fontWeight={800}>Live Orders</Typography>
            <Chip size="small" label={isSocketConnected ? "Connected" : "Reconnecting"} color={isSocketConnected ? "success" : "warning"} variant="outlined" />
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
            {kitchen && <FormControlLabel sx={{ mr: 1 }} label={<Typography variant="body2" fontWeight={700}>Kitchen {kitchen.operationalStatus === "OPEN" ? "open" : kitchen.operationalStatus === "TEMPORARILY_CLOSED" ? "paused" : "off-hours"}</Typography>}
              control={<Switch size="small" checked={Boolean(kitchen.rawIsOpen !== undefined ? kitchen.rawIsOpen : kitchen.isOpen)} disabled={togglingStatus} onChange={event => void handleToggleSwitch(event.target.checked)} />} />}
            <ToggleButtonGroup value={viewMode} exclusive onChange={handleViewModeChange} size="small" aria-label="Order layout" sx={{ mr: 1 }}>
              <ToggleButton value="KANBAN" aria-label="Board view"><ViewKanbanIcon fontSize="small" /></ToggleButton>
              <ToggleButton value="GRID" aria-label="Grid view"><GridViewIcon fontSize="small" /></ToggleButton>
            </ToggleButtonGroup>
            <Tooltip title={sound.enabled ? "Mute order alerts" : "Unmute order alerts"}><IconButton aria-label={sound.enabled ? "Mute order alerts" : "Unmute order alerts"} onClick={() => sound.setEnabled(!sound.enabled)}>{sound.enabled ? <Volume2 size={20} /> : <VolumeX size={20} />}</IconButton></Tooltip>
            <Tooltip title="Refresh orders"><span><IconButton aria-label="Refresh orders" disabled={refreshing} onClick={() => void fetchQueue()}>{refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}</IconButton></span></Tooltip>
            <Tooltip title="Order settings"><IconButton aria-label="Order settings" onClick={() => setSettingsOpen(true)}><Settings2 size={20} /></IconButton></Tooltip>
          </Stack>
        </Stack>
        <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>Order settings<IconButton aria-label="Close order settings" onClick={() => setSettingsOpen(false)} sx={{ position: "absolute", right: 12, top: 12 }}><X size={20} /></IconButton></DialogTitle>
          <DialogContent dividers><Stack spacing={3}>
            <Box><Typography fontWeight={700} mb={1}>Order alerts</Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ minWidth: 0, maxWidth: "100%" }}>
              <FormControlLabel label="Sound" control={<Switch checked={sound.enabled} onChange={(_, checked) => sound.setEnabled(checked)} size="small" />} sx={{ mr: 0 }} />
              <FormControl size="small" sx={{ width: 160, maxWidth: "100%" }} disabled={!sound.enabled}>
                <InputLabel id="order-sound-label">Order sound</InputLabel>
                <Select labelId="order-sound-label" label="Order sound" value={sound.preset} onChange={event => sound.setPreset(event.target.value)}>
                  {ORDER_SOUND_PRESETS.map(preset => <MenuItem key={preset.id} value={preset.id}>{preset.label}</MenuItem>)}
                  {sound.customSound && <MenuItem value="custom">Custom sound</MenuItem>}
                </Select>
              </FormControl>
              <Tooltip title={sound.customSound ? "Replace custom sound" : "Upload custom sound"}>
                <IconButton component="label" aria-label="Upload custom sound">
                  <Upload size={20} />
                  <input hidden type="file" accept="audio/*,.mp3,.wav,.ogg,.m4a" onChange={event => {
                    const file = event.target.files?.[0];
                    event.target.value = "";
                    if (file) void sound.uploadCustomSound(file);
                  }} />
                </IconButton>
              </Tooltip>
              <Slider aria-label="Order alert volume" value={sound.volume} onChange={(_, value) => sound.setVolume(value as number)} min={0} max={100}
                disabled={!sound.enabled} valueLabelDisplay="auto" sx={{ width: 90 }} />
              <Tooltip title={sound.blocked ? "Enable and test sound" : "Test order sound"}>
                <span><IconButton aria-label="Test order sound" disabled={!sound.enabled || sound.volume === 0} onClick={() => sound.play(true)}><Volume2 size={20} /></IconButton></span>
              </Tooltip>
              {sound.enabled && sound.blocked && <Typography variant="caption" color="warning.main">Sound blocked</Typography>}
              {sound.customError && <Typography variant="caption" color="error">{sound.customError}</Typography>}
            </Stack>

            </Box>
            {kitchen && <FormControlLabel label="Rain mode" control={<Switch checked={Boolean(kitchen.logisticsConfig?.isRainMode)} disabled={togglingRain} onChange={event => void handleToggleRainMode(event.target.checked)} />} />}
            <FormControlLabel control={<Switch checked={pollingEnabled} onChange={event => setPollingEnabled(event.target.checked)} />} label="Automatic queue refresh" />
          </Stack></DialogContent>
        </Dialog>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ xs: "stretch", md: "center" }} mb={2} sx={{ pb: 2, borderBottom: "1px solid #e2e8f0" }}>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ flex: 1 }}>
            <Typography variant="body2"><strong>{stats.total}</strong> {debouncedSearch || status ? "matching" : "active"}</Typography>
            <Typography variant="body2" color={overdueCount ? "error.main" : "text.secondary"}><strong>{overdueCount}</strong> overdue</Typography>
            <Typography variant="body2" color="text.secondary">Oldest waiting: <strong>{oldestWaiting === null ? "--" : oldestWaiting + "m"}</strong></Typography>
            <Typography variant="caption" color={!isSocketConnected && lastUpdated && clock - lastUpdated > 60000 ? "warning.main" : "text.secondary"}>{lastUpdated ? `Updated ${Math.max(0, Math.floor((clock - lastUpdated) / 1000))}s ago` : "Waiting for updates"}</Typography>
          </Stack>
          <TextField size="small" label="Search orders" value={search} onChange={event => setSearch(event.target.value)} sx={{ width: { xs: "100%", md: 250 } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
          <FormControl size="small" sx={{ minWidth: 155 }}>
            <InputLabel id="queue-status-label">Order status</InputLabel><Select labelId="queue-status-label" value={status} label="Order status" onChange={event => setStatus(event.target.value)}>
              <MenuItem value="">All stages</MenuItem>
              {queueStatuses.map(option => <MenuItem key={option} value={option}>{option.replace("_", " ")}</MenuItem>)}
            </Select>
          </FormControl>
        </Stack>
        {sound.enabled && sound.blocked && <Alert severity="warning" sx={{ mb: 1.5 }} action={<Button size="small" onClick={() => sound.play(true)}>Enable sound</Button>}>Order audio needs permission</Alert>}
        {!isSocketConnected && <Alert severity="warning" sx={{ mb: 1.5 }}>{pollingEnabled ? "Live connection interrupted. Orders refresh every 15 seconds." : "Live connection interrupted. Automatic refresh is off."}</Alert>}
        {narrowBoard && viewMode === "KANBAN" && <ToggleButtonGroup exclusive value={activeStage} onChange={(_, value) => { if (value) setActiveStage(value); }} aria-label="Order stage" sx={{ width: "100%", mb: 1.5 }}>
          {kanbanStages.map(stage => <ToggleButton key={stage.id} value={stage.id} sx={{ flex: 1, minWidth: 0, px: 0.5, fontSize: 12, lineHeight: 1.3 }}>{stage.id === "new" ? "New" : stage.id === "prep" ? "Kitchen" : stage.id === "ready" ? "Ready" : "Delivery"} ({orders.filter(order => stage.statuses.includes(order.status)).length})</ToggleButton>)}
        </ToggleButtonGroup>}

        {/* Loading Spinner */}
        {loading && orders.length === 0 ? (
          <Box display="flex" justifyContent="center" py={12}>
            <CircularProgress />
          </Box>
        ) : viewMode === "KANBAN" ? (
          /* Kanban Board Pipeline Layout */
          <Box
            ref={boardRef}
            data-testid="live-orders-board"
            sx={{
              display: "grid",
              gridTemplateColumns: narrowBoard ? "minmax(0, 1fr)" : "repeat(4, minmax(0, 1fr))",
              gap: 1.5,
              height: boardHeight,
              minHeight: 280,
              alignItems: "stretch",
              "&::-webkit-scrollbar": {
                height: 8,
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#cbd5e1",
                borderRadius: 4,
              },
            }}
          >
            {kanbanStages.filter(stage => !narrowBoard || stage.id === activeStage).map((stage) => {
              const stageOrders = orders.filter((o) => stage.statuses.includes(o.status));
              return (
                <Paper
                  key={stage.id}
                  variant="outlined"
                  sx={{
                    minWidth: 0,
                    height: "100%",
                    borderRadius: 2,
                    backgroundColor: "#f8fafc",
                    borderColor: stage.borderColor,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    boxShadow: "0 4px 14px rgba(15,23,42,0.04)",
                  }}
                >
                  {/* Column Header */}
                  <Box
                    sx={{
                      p: 1.5,
                      backgroundColor: stage.headerBg,
                      borderBottom: `1px solid ${stage.borderColor}`,
                      borderTop: `3px solid ${stage.headerAccent}`,
                      flexShrink: 0,
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" spacing={1} alignItems="center">
                        {stage.icon}
                        <Typography variant="body2" fontWeight={800} color="#0f172a">
                          {stage.title}
                        </Typography>
                      </Stack>
                      <Chip
                        label={stageOrders.length}
                        size="small"
                        sx={{
                          fontWeight: 850,
                          backgroundColor: stageOrders.length > 0 ? stage.headerAccent : "#e2e8f0",
                          color: stageOrders.length > 0 ? "#ffffff" : "#64748b",
                        }}
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, fontWeight: 500 }}>
                      {stage.subtitle}
                    </Typography>
                  </Box>

                  {/* Column Card Stack */}
                  <Box
                    sx={{
                      p: 1.5,
                      flex: 1,
                      overflowY: "auto",
                      minHeight: 0,
                      overscrollBehavior: "contain",
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                    }}
                  >
                    {stageOrders.length === 0 ? (
                      <Box
                        sx={{
                          py: 5,
                          px: 1,
                          textAlign: "center",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                          {stage.emptyText}
                        </Typography>
                      </Box>
                    ) : (
                      stageOrders.map((order) => (
                        <OrderCard
                          key={order._id}
                          order={order}
                          orders={orders}
                          updatingOrderId={updatingOrderId}
                          handleStatusUpdate={handleStatusUpdate}
                          riderDrafts={riderDrafts}
                          setRiderDrafts={setRiderDrafts}
                          deliveryAgents={deliveryAgents}
                          handleAssignRider={handleAssignRider}
                          setSelectedOrder={setSelectedOrder}
                          isAdminUser={isAdminUser}
                          isKanban={true}
                          kitchen={kitchen}
                        />
                      ))
                    )}
                  </Box>
                </Paper>
              );
            })}
          </Box>
        ) : (
          /* Classic Compact Grid Layout */
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                lg: "repeat(2, minmax(0, 1fr))",
              },
              gap: 2.5,
            }}
          >
            {orders.map((order) => (
              <OrderCard
                key={order._id}
                order={order}
                orders={orders}
                updatingOrderId={updatingOrderId}
                handleStatusUpdate={handleStatusUpdate}
                riderDrafts={riderDrafts}
                setRiderDrafts={setRiderDrafts}
                deliveryAgents={deliveryAgents}
                handleAssignRider={handleAssignRider}
                setSelectedOrder={setSelectedOrder}
                isAdminUser={isAdminUser}
                isKanban={false}
                kitchen={kitchen}
              />
            ))}

            {orders.length === 0 && (
              <Paper
                variant="outlined"
                sx={{
                  p: 8,
                  borderRadius: 3,
                  textAlign: "center",
                  width: "100%",
                  gridColumn: "1 / -1",
                  border: "1px dashed #cbd5e1",
                  backgroundColor: "#ffffff",
                }}
              >
                <CheckCircleOutlineIcon sx={{ fontSize: 42, color: "#94a3b8", mb: 1.5 }} />
                <Typography variant="subtitle1" color="text.primary" fontWeight={750}>
                  Dispatch Queue is Clear
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  All active instant orders have been processed and fulfilled.
                </Typography>
              </Paper>
            )}
          </Box>
        )}

        {/* Order Details Modal */}
        <Dialog open={Boolean(selectedOrder)} onClose={() => setSelectedOrder(null)} fullWidth maxWidth="md">
          <DialogTitle sx={{ fontWeight: 850 }}>Order #{selectedOrder?.orderNumber} Full Details</DialogTitle>
          <DialogContent dividers>
            {selectedOrder && (
              <Stack spacing={2}>
                <Typography variant="subtitle1" fontWeight={800}>{selectedOrder.orderNumber}</Typography>
                <Typography color="text.secondary">{selectedOrder.deliveryAddress?.addressLine}</Typography>
                {selectedOrder.deliveryAddress?.landmark && <Typography variant="body2">Landmark: {selectedOrder.deliveryAddress.landmark}</Typography>}
                <Typography variant="body2">
                  Customer: <strong>{selectedOrder.userId?.name}</strong> {selectedOrder.userId?.phoneNumber ? `• ${selectedOrder.userId.phoneNumber}` : ""}
                </Typography>
                <Typography variant="body2">
                  {isAdminUser ? `Total Paid: ₹${selectedOrder.totalPaid} • ` : ""}Payment Method: <strong>{selectedOrder.paymentMethod}</strong>
                </Typography>
                {selectedOrder.riderId && (
                  <Typography variant="body2">
                    Rider: <strong>{selectedOrder.riderId.name}</strong> {selectedOrder.riderId.phoneNumber ? `• ${selectedOrder.riderId.phoneNumber}` : ""}
                  </Typography>
                )}
                {selectedOrder.notes && (
                  <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: "#fffbeb", border: "1px solid #fde68a" }}>
                    <Typography variant="caption" color="warning.main" fontWeight={850}>
                      Instructions / Customer Notes:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#78350f" }}>
                      {selectedOrder.notes}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography fontWeight={800} mb={1}>Items Breakdown</Typography>
                  <Stack spacing={1}>
                    {[...(selectedOrder.items || []), ...(selectedOrder.addOns || [])].map((item: any, index: number) => (
                      <Paper key={`${item.instantMealId || item.name}-${index}`} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                        <Stack direction="row" justifyContent="space-between">
                          <Box>
                            <Typography fontWeight={700}>{item.name}</Typography>
                            {item.selectedSide && (
                              <Typography variant="body2" color="warning.main" sx={{ fontWeight: 650, mt: 0.25, mb: 0.25 }}>
                                Side Choice: {item.selectedSide.name} ({item.selectedSide.price > 0 ? `+₹${item.selectedSide.price}` : "Included"})
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary">
                              Qty {item.quantity} {isAdminUser && `• ₹${item.price} each`}
                            </Typography>
                          </Box>
                          {isAdminUser && <Typography fontWeight={800}>₹{item.totalPrice}</Typography>}
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                </Box>
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSelectedOrder(null)} sx={{ fontWeight: 750 }}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Manual offline confirmation dialog */}
        <Dialog open={toggleDialogOpen} onClose={() => setToggleDialogOpen(false)}>
          <DialogTitle sx={{ fontWeight: 850 }}>Deactivate Instant Ordering?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to go offline? Active orders will not be affected. New instant orders will be blocked immediately.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setToggleDialogOpen(false)} disabled={togglingStatus}>Cancel</Button>
            <Button onClick={() => void confirmGoOffline()} color="error" variant="contained" disabled={togglingStatus}>
              {togglingStatus ? "Toggling..." : "Go Offline"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default InstantOrdersQueue;
